import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPinIcon, PhoneIcon, MailIcon } from 'lucide-react';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.7,
    ease: 'easeOut'
  }
};
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <motion.div {...fadeInUp} className="mb-10" data-id="element-399">
      <h2 className="font-serif text-2xl md:text-3xl font-bold text-brand-green mb-4" data-id="element-400">
        {title}
      </h2>
      <div className="space-y-4 text-gray-600 leading-relaxed" data-id="element-401">{children}</div>
    </motion.div>;
}
function BulletList({
  items
}: {
  items: string[];
}) {
  return <ul className="space-y-2" data-id="element-402">
      {items.map(item => <li key={item} className="flex items-start" data-id="element-403">
          <span className="mt-2 mr-3 h-1.5 w-1.5 rounded-full bg-brand-gold shrink-0" data-id="element-404"></span>
          <span data-id="element-405">{item}</span>
        </li>)}
    </ul>;
}
export function Privacy() {
  return <div className="w-full pt-24 bg-brand-offwhite" data-id="element-406">
      {/* Page Header */}
      <section className="relative py-20 bg-brand-charcoal text-white overflow-hidden" data-id="element-407">
        <div className="absolute inset-0 z-0 opacity-20" data-id="element-408">
          <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80&w=2000" alt="" aria-hidden="true" className="w-full h-full object-cover" data-id="element-409" />
        </div>
        <div className="relative z-10 max-w-container mx-auto px-6 text-center" data-id="element-410">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="font-serif text-5xl md:text-6xl font-bold mb-4" data-id="element-411">
            Privacy Policy
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-sm uppercase tracking-[0.2em] text-brand-gold" data-id="element-412">
            Last Updated: June 2026
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20" data-id="element-413">
        <div className="max-w-3xl mx-auto px-6" data-id="element-414">
          <motion.p {...fadeInUp} className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-415">
            McErick Attorneys ("we," "our," or "us") is committed to protecting
            the privacy and confidentiality of our clients, website visitors,
            and business partners. This Privacy Policy explains how we collect,
            use, store, and protect your personal information when you use our
            website or engage our legal services.
          </motion.p>
          <motion.p {...fadeInUp} className="text-lg text-gray-600 leading-relaxed mb-12" data-id="element-416">
            By using our website, you agree to the practices described in this
            Privacy Policy.
          </motion.p>

          <Section title="Information We Collect" data-id="element-417">
            <p data-id="element-418">We may collect the following information:</p>
            <BulletList items={['Full name', 'Email address', 'Telephone number', 'Company or organization (where applicable)', 'Information submitted through contact or consultation forms', 'Information relating to legal enquiries', 'Technical information such as browser type, device information, IP address, and website usage statistics']} data-id="element-419" />
          </Section>

          <Section title="How We Use Your Information" data-id="element-420">
            <p data-id="element-421">We use your information to:</p>
            <BulletList items={['Respond to enquiries and consultation requests', 'Provide legal services', 'Communicate with clients', 'Improve our website and services', 'Comply with legal and regulatory obligations', 'Protect our legal rights and interests']} data-id="element-422" />
          </Section>

          <Section title="Confidentiality" data-id="element-423">
            <p data-id="element-424">
              As a legal practice, confidentiality is fundamental to our work.
              Information provided through this website will be handled with
              appropriate care and professional responsibility.
            </p>
            <p data-id="element-425">
              Submitting information through this website does not automatically
              create an attorney-client relationship.
            </p>
          </Section>

          <Section title="Cookies" data-id="element-426">
            <p data-id="element-427">
              Our website may use cookies to improve user experience, remember
              visitor preferences, and analyze website traffic.
            </p>
            <p data-id="element-428">
              You may disable cookies through your browser settings if you
              prefer.
            </p>
          </Section>

          <Section title="Information Sharing" data-id="element-429">
            <p data-id="element-430">We do not sell, rent, or trade your personal information.</p>
            <p data-id="element-431">Information may only be disclosed:</p>
            <BulletList items={['Where required by law', 'To comply with legal obligations', 'To protect our legal rights', 'With your consent', 'To trusted service providers assisting us in operating our website or business, subject to confidentiality obligations']} data-id="element-432" />
          </Section>

          <Section title="Data Security" data-id="element-433">
            <p data-id="element-434">
              We implement reasonable administrative, technical, and
              organizational safeguards to protect personal information from
              unauthorized access, disclosure, alteration, or destruction.
            </p>
            <p data-id="element-435">
              While we take appropriate security measures, no online
              transmission or storage system can be guaranteed to be completely
              secure.
            </p>
          </Section>

          <Section title="Third-Party Links" data-id="element-436">
            <p data-id="element-437">
              Our website may contain links to third-party websites. McErick
              Attorneys is not responsible for the privacy practices or content
              of external websites.
            </p>
          </Section>

          <Section title="Your Rights" data-id="element-438">
            <p data-id="element-439">Subject to applicable laws, you may request to:</p>
            <BulletList items={['Access your personal information', 'Correct inaccurate information', 'Request deletion where legally permissible', 'Withdraw consent where applicable', 'Raise concerns regarding your personal information']} data-id="element-440" />
          </Section>

          <Section title="Changes to This Policy" data-id="element-441">
            <p data-id="element-442">
              We may update this Privacy Policy from time to time. Changes will
              be published on this page with an updated revision date.
            </p>
          </Section>

          <Section title="Contact Us" data-id="element-443">
            <p data-id="element-444">
              If you have questions regarding this Privacy Policy, please{' '}
              <Link to="/contact" className="text-brand-green font-medium hover:text-brand-gold transition-colors" data-id="element-445">
                contact us
              </Link>
              .
            </p>
            <div className="mt-6 rounded-2xl bg-white border border-gray-100 shadow-soft p-8 space-y-4" data-id="element-446">
              <p className="font-serif text-xl font-bold text-brand-charcoal" data-id="element-447">
                McErick Attorneys
              </p>
              <div className="flex items-start text-sm" data-id="element-448">
                <MapPinIcon className="w-5 h-5 mr-3 text-brand-gold shrink-0 mt-0.5" data-id="element-449" />
                <span data-id="element-450">
                  Plot 70667, 7th Floor, The Tower, Farscape Precinct,
                  Fairgrounds Office Park, Gaborone, Botswana
                  <br data-id="element-451" />
                  P.O. Box 612 AAH, Masa, Gaborone
                </span>
              </div>
              <a href="tel:+2673165472" className="flex items-center text-sm hover:text-brand-green transition-colors" data-id="element-452">
                <PhoneIcon className="w-5 h-5 mr-3 text-brand-gold shrink-0" data-id="element-453" />
                Telephone: (+267) 316 5472
              </a>
              <a href="tel:+26772553210" className="flex items-center text-sm hover:text-brand-green transition-colors" data-id="element-454">
                <PhoneIcon className="w-5 h-5 mr-3 text-brand-gold shrink-0" data-id="element-455" />
                Mobile: (+267) 7255 3210
              </a>
              <a href="mailto:charles@mcerickattorneys.co.bw" className="flex items-center text-sm hover:text-brand-green transition-colors" data-id="element-456">
                <MailIcon className="w-5 h-5 mr-3 text-brand-gold shrink-0" data-id="element-457" />
                charles@mcerickattorneys.co.bw
              </a>
            </div>
          </Section>
        </div>
      </section>
    </div>;
}